/**
 * RealtimeReconnectManager
 * 
 * Wraps Supabase Realtime channels with exponential backoff reconnection.
 * Integrates with OfflineBanner via a shared reactive state.
 * 
 * Backoff: 1s → 2s → 4s → 8s → 16s → 30s → 60s (max) + jitter
 * Triggers: visibilitychange, online event, channel error/timeout
 */

import { supabase } from '@/integrations/supabase/client';
import type { RealtimeChannel } from '@supabase/supabase-js';

export interface ReconnectState {
  isConnected: boolean;
  failedAttempts: number;
  lastError: string | null;
}

type StateListener = (state: ReconnectState) => void;

const MAX_BACKOFF_MS = 60_000;
const MAX_FAILED_ATTEMPTS = 20;
const JITTER_RANGE = 500;
const RAPID_CYCLE_THRESHOLD_MS = 5_000; // If subscribed < 5s ago, it's a rapid cycle

// Global reconnect state - shared across all managed channels
let globalState: ReconnectState = {
  isConnected: true,
  failedAttempts: 0,
  lastError: null,
};

const listeners = new Set<StateListener>();

function notifyListeners() {
  for (const fn of listeners) {
    try { fn(globalState); } catch { /* non-critical */ }
  }
}

export function subscribeReconnectState(fn: StateListener): () => void {
  listeners.add(fn);
  fn(globalState); // immediate state
  return () => { listeners.delete(fn); };
}

function getBackoff(attempt: number): number {
  const base = Math.min(1000 * Math.pow(2, attempt), MAX_BACKOFF_MS);
  const jitter = Math.random() * JITTER_RANGE;
  return base + jitter;
}

interface ManagedChannel {
  name: string;
  channel: RealtimeChannel;
  retryTimer: ReturnType<typeof setTimeout> | null;
  failedAttempts: number;
  lastSubscribedAt: number; // timestamp of last successful SUBSCRIBED event
  createChannel: () => RealtimeChannel;
}

const managedChannels = new Map<string, ManagedChannel>();

/**
 * Create a managed Realtime channel with auto-reconnection.
 * Returns the channel and an unsubscribe function.
 */
export function createManagedChannel(
  channelName: string,
  setup: (channel: RealtimeChannel) => RealtimeChannel,
): { channel: RealtimeChannel; unsubscribe: () => void } {
  // Clean up existing channel with same name
  const existing = managedChannels.get(channelName);
  if (existing) {
    if (existing.retryTimer) clearTimeout(existing.retryTimer);
    supabase.removeChannel(existing.channel);
    managedChannels.delete(channelName);
  }

  const createChannel = (): RealtimeChannel => {
    // Suffix with a unique id so re-creates never collide with a cached
    // already-subscribed channel (which would throw
    // "cannot add postgres_changes callbacks after subscribe()").
    const uniqueName = `${channelName}-${Math.random().toString(36).slice(2)}-${Date.now()}`;
    const ch = supabase.channel(uniqueName);
    return setup(ch);
  };

  let channel = createChannel();

  const managed: ManagedChannel = {
    name: channelName,
    channel,
    retryTimer: null,
    failedAttempts: 0,
    lastSubscribedAt: 0,
    createChannel,
  };

  managedChannels.set(channelName, managed);

  // Subscribe with status monitoring
  channel.subscribe((status) => {
    if (status === 'SUBSCRIBED') {
      handleSubscribed(managed);
    } else if (status === 'CLOSED' || status === 'CHANNEL_ERROR' || status === 'TIMED_OUT') {
      updateGlobalConnected(false, `Channel ${channelName}: ${status}`);
      scheduleReconnect(managed);
    }
  });

  const unsubscribe = () => {
    if (managed.retryTimer) clearTimeout(managed.retryTimer);
    supabase.removeChannel(managed.channel);
    managedChannels.delete(channelName);
  };

  return { channel, unsubscribe };
}

function handleSubscribed(managed: ManagedChannel) {
  const now = Date.now();
  const timeSinceLastSubscribe = now - managed.lastSubscribedAt;

  // Rapid-cycle detection: if we were SUBSCRIBED very recently but disconnected
  // and reconnected again, it means the channel is flapping. Don't reset failedAttempts.
  if (managed.lastSubscribedAt > 0 && timeSinceLastSubscribe < RAPID_CYCLE_THRESHOLD_MS) {
    // This is a rapid cycle - increment failures instead of resetting
    managed.failedAttempts = Math.max(managed.failedAttempts, 1) + 1;
    console.warn(`[Reconnect] ${managed.name} rapid cycle detected (${timeSinceLastSubscribe}ms), backoff attempt #${managed.failedAttempts}`);
  } else {
    // Genuine stable connection - reset
    managed.failedAttempts = 0;
  }

  managed.lastSubscribedAt = now;
  updateGlobalConnected(true);
}

function scheduleReconnect(managed: ManagedChannel) {
  if (managed.retryTimer) clearTimeout(managed.retryTimer);
  if (managed.failedAttempts >= MAX_FAILED_ATTEMPTS) {
    console.warn(`[Reconnect] Giving up on ${managed.name} after ${MAX_FAILED_ATTEMPTS} attempts`);
    return;
  }

  const delay = getBackoff(managed.failedAttempts);
  managed.failedAttempts++;

  console.log(`[Reconnect] ${managed.name} retry #${managed.failedAttempts} in ${Math.round(delay)}ms`);

  managed.retryTimer = setTimeout(() => {
    // Remove old channel
    supabase.removeChannel(managed.channel);

    // Create fresh channel
    const newChannel = managed.createChannel();
    managed.channel = newChannel;

    newChannel.subscribe((status) => {
      if (status === 'SUBSCRIBED') {
        handleSubscribed(managed);
        console.log(`[Reconnect] ${managed.name} reconnected`);
      } else if (status === 'CLOSED' || status === 'CHANNEL_ERROR' || status === 'TIMED_OUT') {
        updateGlobalConnected(false, `Channel ${managed.name}: ${status}`);
        scheduleReconnect(managed);
      }
    });
  }, delay);
}

function updateGlobalConnected(connected: boolean, error?: string) {
  if (connected) {
    globalState = { isConnected: true, failedAttempts: 0, lastError: null };
  } else {
    const totalFailed = Array.from(managedChannels.values())
      .reduce((sum, m) => sum + m.failedAttempts, 0);
    globalState = {
      isConnected: false,
      failedAttempts: totalFailed,
      lastError: error || globalState.lastError,
    };
  }
  notifyListeners();
}

// ─── Global triggers: visibility + online ─────────────────────

function handleVisibilityChange() {
  if (document.visibilityState === 'visible') {
    // Reconnect all channels when app returns to foreground
    for (const managed of managedChannels.values()) {
      if (managed.failedAttempts > 0) {
        scheduleReconnect(managed);
      }
    }
  }
}

function handleOnline() {
  console.log('[Reconnect] Network online - reconnecting all channels');
  for (const managed of managedChannels.values()) {
    // Reset failures and reconnect immediately
    managed.failedAttempts = 0;
    scheduleReconnect(managed);
  }
}

// Register global listeners once
if (typeof window !== 'undefined') {
  document.addEventListener('visibilitychange', handleVisibilityChange);
  window.addEventListener('online', handleOnline);
}
